// v59 security-blocks API client.

import useSWR from 'swr';

export type BlockType = 'path' | 'sha256' | 'pattern' | 'package';

export interface SecurityBlock {
  id: string;
  repo_id?: string | null;
  block_type: BlockType;
  pattern: string;
  reason: string;
  // v185 — long-form developer-facing message. Optional;
  // when empty, the 403 plain-text body falls back to `reason`.
  customer_message?: string;
  cve_id?: string;
  blocked_until?: string | null;
  active: boolean;
  created_by: string;
  created_at: string;
}

export interface SecurityAuditRow {
  id: number;
  artifact_path: string;
  artifact_sha256?: string;
  blocked_at: string;
  requester_ip?: string;
  requester_user?: string;
  reason: string;
  cve_id?: string;
  block_type?: string;
}

const json = (url: string) => fetch(url, { credentials: 'include' }).then((r) => r.json());

export function useSecurityBlocks(scope: string = '*') {
  return useSWR<SecurityBlock[]>(
    `/api/admin/security?repo_id=${encodeURIComponent(scope)}`,
    json,
    { refreshInterval: 30_000 },
  );
}

export function useSecurityAudit(limit = 100) {
  return useSWR<SecurityAuditRow[]>(
    `/api/admin/security/audit?limit=${limit}`,
    json,
    { refreshInterval: 15_000 },
  );
}

// v189 reverse-dependency search.
//
// Each row is one (principal_email, sha256) group rolled up by the
// backend. The `recent` array is reserved for a future drill-down
// pane; the v1 list view ignores it.
export interface ArtifactPullGroup {
  principal_kind: 'user' | 'service_account' | 'anonymous';
  principal_email: string;
  sha256: string;
  artifact_path?: string;
  repo_slug?: string;
  project_slug?: string;
  package_name?: string;
  count: number;
  distinct_ips: number;
  first_pulled_at: string;
  last_pulled_at: string;
}

export interface PullSearchResponse {
  groups: ArtifactPullGroup[];
  since: string;
}

export interface PullSearchFilters {
  package?: string;
  sha256?: string;
  cve?: string;
  since?: string;
  principal?: string;
}

// usePullSearch is null-keyed when no filter is set so the page
// doesn't fire a 30-day-wide query the moment it loads.
export function usePullSearch(filters: PullSearchFilters | null) {
  const key =
    filters === null
      ? null
      : (() => {
          const p = new URLSearchParams();
          if (filters.package) p.set('package', filters.package);
          if (filters.sha256) p.set('sha256', filters.sha256);
          if (filters.cve) p.set('cve', filters.cve);
          if (filters.since) p.set('since', filters.since);
          if (filters.principal) p.set('principal', filters.principal);
          return `/api/admin/security/pulls?${p.toString()}`;
        })();
  return useSWR<PullSearchResponse>(key, json);
}

export async function createSecurityBlock(input: {
  block_type: BlockType;
  pattern: string;
  reason: string;
  customer_message?: string;
  cve_id?: string;
  repo_id?: string | null;
  blocked_until?: string | null;
  active: boolean;
}): Promise<SecurityBlock> {
  const res = await fetch('/api/admin/security', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`create block: ${res.status}`);
  return res.json();
}

export async function deleteSecurityBlock(id: string): Promise<void> {
  const res = await fetch(`/api/admin/security/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`delete block: ${res.status}`);
}

export async function toggleSecurityBlock(id: string, active: boolean): Promise<void> {
  const res = await fetch(`/api/admin/security/${id}/active`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) throw new Error(`toggle block: ${res.status}`);
}

// v185 — edit the developer-facing customer_message inline,
// without recreating the block.
export async function updateSecurityBlockMessage(
  id: string,
  customer_message: string,
): Promise<void> {
  const res = await fetch(`/api/admin/security/${id}/message`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_message }),
  });
  if (!res.ok) throw new Error(`update block message: ${res.status}`);
}

// --- v160 dry-run preview ---

export interface PreviewMatch {
  artifact_id: string;
  url: string;
  path: string;
  version?: string;
  sha256: string;
  size_bytes: number;
  uploaded_at: string;
  repo_id: string;
  repo_slug: string;
  project_slug: string;
  format: string;
}

export interface PreviewByRepo {
  repo_id: string;
  repo_slug: string;
  project_slug: string;
  format: string;
  count: number;
}

export interface OverlappingBlock {
  id: string;
  block_type: BlockType;
  pattern: string;
  reason: string;
}

export interface PreviewResult {
  block_type: BlockType;
  pattern: string;
  repo_scope?: string | null;
  match_count: number;
  // True when match_count was capped at the server's 10 000 row probe
  // limit. The UI should render the count as "10000+" in that case.
  count_capped: boolean;
  sample: PreviewMatch[];
  by_repo: PreviewByRepo[];
  would_overlap?: OverlappingBlock[];
}

// previewSecurityBlock runs the dry-run match for a candidate block
// without persisting anything. Returns the artifact set the runtime
// would 403 if the block were created with the same (type, pattern).
export async function previewSecurityBlock(input: {
  block_type: BlockType;
  pattern: string;
  repo_id?: string | null;
}): Promise<PreviewResult> {
  const res = await fetch('/api/admin/security/preview', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`preview block: ${res.status} ${await res.text()}`);
  return res.json();
}
